import { uploadHint, PHOTO_ACCEPT } from '@theme/pb-upload-policy';
/**
 * PB-FND-72 — AI Image field plugin (registry adapter).
 * Extracted from pb-field-runtime; wired via createAiImageAdapter(deps).
 */

/**
 * PB-AIRS-5 — Review ownership.
 *
 * A character only owes a review when the job that produced it finished in **this**
 * builder session. Characters that arrive from storage (draft resume, cart line,
 * bundle-row sync belt) are restored, not newly generated — locked decision: restored
 * counts as already reviewed. Same shape as PB-AIT-12 job ownership on AI Text.
 */
const REVIEW_SESSION_ID =
  'pbrs-' +
  Date.now().toString(36) +
  '-' +
  Math.random().toString(36).slice(2, 10);

/** Id for the current page load — stamped on characters generated here. */
export function getReviewSessionId() {
  return REVIEW_SESSION_ID;
}

/**
 * True when this character was generated in the current session and still owes a review.
 * @param {object|null|undefined} state — wrap._pbAiState or savedAiEntryToReadinessState output
 */
export function isReviewOwedForCharacter(state) {
  if (!state || typeof state !== 'object') return false;
  const stamp = state.reviewPendingFrom != null ? String(state.reviewPendingFrom).trim() : '';
  return !!stamp && stamp === REVIEW_SESSION_ID;
}

function parseAiRequired(cfg) {
  if (!cfg) return false;
  if (cfg.required === true || cfg.required === 'true') return true;
  return false;
}

function fieldTypeLower(field) {
  return String(field?.type || '').toLowerCase();
}

function isAiImageField(field) {
  return fieldTypeLower(field).includes('ai image');
}

/** Parse AI Image field settings from BUILDER_DATA field config. */
export function parseAiFieldSettings(field) {
  if (!field) return {};
  const raw = field.settings;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const merged = { ...raw };
    if (raw.value != null && typeof raw.value === 'string' && raw.value.trim()) {
      try {
        if (raw.value.trim().charAt(0) === '{') {
          Object.assign(merged, JSON.parse(raw.value));
        }
      } catch (e) {}
    }
    return merged;
  }
  const cfg = {};
  const s = String(raw || '');
  if (s.trim().charAt(0) === '{') {
    try {
      return JSON.parse(s);
    } catch (e) {
      return cfg;
    }
  }
  s.split(/\r?\n/).forEach(function (line) {
    line = line.trim();
    if (!line) return;
    const idx = line.indexOf(':');
    if (idx === -1) return;
    const key = line.slice(0, idx).trim().replace(/\s+/g, '_');
    const val = line.slice(idx + 1).trim();
    if (key) cfg[key] = val;
  });
  return cfg;
}

/**
 * Map persisted aiImage.byLabel entry → readiness state for computeAiImageReadiness.
 * @param {object|null|undefined} saved
 */
/**
 * Stable id for "this exact character version" — session-scoped review ack (cart edit + PDP).
 * @param {object|null|undefined} state — wrap._pbAiState or savedAiEntryToReadinessState output
 */
export function buildCharacterReviewFingerprint(state) {
  if (!state || typeof state !== 'object') return '';
  const photoUrl = state.photoUrl != null ? String(state.photoUrl).trim() : '';
  const urls = Array.isArray(state.characterUrls) ? state.characterUrls : [];
  const idx = Math.max(
    0,
    Math.min(Number(state.selectedIndex) || 0, Math.max(urls.length - 1, 0))
  );
  let characterUrl = urls[idx] ? String(urls[idx]).trim() : '';
  if (!characterUrl && state.characterUrl != null) {
    characterUrl = String(state.characterUrl).trim();
  }
  if (!characterUrl) return '';
  const jobId = state.jobId != null ? String(state.jobId).trim() : '';
  return [photoUrl, characterUrl, jobId].join('\u001f');
}

export function isAiJobActivelyRunning(state) {
  if (!state) return false;
  if (state.isGenerating) return true;
  const status = state.jobStatus || 'idle';
  return status === 'queued' || status === 'running';
}

export function savedAiEntryToReadinessState(saved) {
  if (!saved || typeof saved !== 'object') return null;
  const characterUrls = (saved.characterUrls || []).filter(
    (u) => u != null && String(u).trim() !== ''
  );
  const photoUrl = saved.photoUrl != null ? String(saved.photoUrl).trim() : '';
  if (!photoUrl && !characterUrls.length && !saved.jobId) return null;

  let jobStatus = saved.jobStatus != null ? String(saved.jobStatus).trim() : '';
  if (!jobStatus && characterUrls.length) jobStatus = 'succeeded';
  if (!jobStatus) jobStatus = 'idle';
  const running =
    jobStatus === 'queued' ||
    jobStatus === 'running' ||
    !!(saved.isGenerating && !characterUrls.length);

  return {
    photoUrl,
    characterUrls,
    selectedIndex: Math.max(
      0,
      Math.min(Number(saved.selectedIndex) || 0, Math.max(characterUrls.length - 1, 0))
    ),
    generationCount: Number(saved.generationCount) || characterUrls.length || (photoUrl ? 1 : 0),
    isGenerating: running,
    jobStatus,
    jobId: saved.jobId != null ? String(saved.jobId).trim() : '',
    aiError: saved.aiError != null ? String(saved.aiError) : '',
    reviewPendingFrom:
      saved.reviewPendingFrom != null ? String(saved.reviewPendingFrom).trim() : '',
  };
}

/**
 * finalizeReady for a bundle / offer row AI field from saved state (no DOM).
 * @param {object} field
 * @param {object|null|undefined} savedEntry
 * @param {boolean} reviewAcknowledged
 */
export function resolveAiFinalizeReadyForSavedField(field, savedEntry, reviewAcknowledged) {
  const cfg = parseAiFieldSettings(field);
  const required = parseAiRequired(cfg);
  if (!required) return true;

  const state = savedAiEntryToReadinessState(savedEntry);
  if (!state) {
    return false;
  }

  const hidden = { dataset: { valid: state.photoUrl ? 'true' : 'false' } };
  return computeAiImageReadiness(state, cfg, hidden, { reviewAcknowledged }).finalizeReady;
}

/**
 * Required AI fields on a bundle row that are not finalizeReady.
 *
 * PB-AIRS-5: "saved character counts as reviewed" is no longer a special case here —
 * review ownership in computeAiImageReadiness gives the same answer for every path.
 *
 * @param {object[]} validationFields
 * @param {Record<string, object>|null|undefined} byLabel
 * @param {{ isFieldRequired?: (field: object) => boolean, reviewAcknowledgedForField?: (label: string) => boolean }} [options]
 */
export function computeBundleRowAiFinalizeGaps(validationFields, byLabel, options) {
  const opts = options || {};
  const isRequired = opts.isFieldRequired || function () {
    return false;
  };
  const reviewForField =
    opts.reviewAcknowledgedForField ||
    function () {
      return false;
    };
  const gaps = [];

  (validationFields || []).forEach(function (field) {
    if (!field || !isAiImageField(field)) return;
    if (!isRequired(field)) return;

    const label = field.label || '';
    const saved = byLabel && label ? byLabel[label] : null;

    if (!resolveAiFinalizeReadyForSavedField(field, saved, !!reviewForField(label))) {
      gaps.push((label || 'ai_image') + ':finalize');
    }
  });

  return gaps;
}

function photoUploaded(state, hidden) {
  if (hidden && hidden.dataset && hidden.dataset.valid === 'true') return true;
  if (state && state.photoUrl && String(state.photoUrl).trim()) return true;
  return false;
}

/**
 * Pure readiness contract for async AI Image (PB-AI-2 + PB-AI-3 review ack).
 * @param {object} state — wrap._pbAiState
 * @param {object} cfg — parsed AI settings
 * @param {{ dataset?: { valid?: string } } | null} [hidden]
 * @param {{ reviewAcknowledged?: boolean } | null} [options]
 * @returns {{ stepNavigable: boolean, finalizeReady: boolean, jobStatus: string, characterReady: boolean, required: boolean }}
 */
export function computeAiImageReadiness(state, cfg, hidden, options) {
  const required = parseAiRequired(cfg);
  const hasPhoto = photoUploaded(state || {}, hidden);
  const jobStatus = (state && state.jobStatus) || 'idle';
  const running =
    !!(state && state.isGenerating) || jobStatus === 'queued' || jobStatus === 'running';
  const hasCharacter = !!(
    state &&
    Array.isArray(state.characterUrls) &&
    state.characterUrls.length > 0
  );
  const failed = jobStatus === 'failed' || !!(state && state.aiError);
  // PB-AIRS-5: a restored character (not generated this session) is already reviewed.
  const reviewAcknowledged =
    !!(options && options.reviewAcknowledged) || !isReviewOwedForCharacter(state);

  let stepNavigable = true;
  if (required) {
    stepNavigable = hasPhoto;
  }

  const characterReady =
    hasCharacter && !running && jobStatus === 'succeeded' && !failed;

  let finalizeReady = true;
  if (required) {
    finalizeReady = characterReady && reviewAcknowledged;
  } else if (hasPhoto) {
    // PB-AIG-3 grill override: optional + upload started → same hard block until character + review.
    if (running || failed) {
      finalizeReady = false;
    } else if (characterReady) {
      finalizeReady = reviewAcknowledged;
    } else {
      finalizeReady = false;
    }
  }

  return { stepNavigable, finalizeReady, jobStatus, characterReady, required };
}

/**
 * @param {HTMLElement} wrap — .pb-ai-image-wrap
 * @returns {{ stepNavigable: boolean, finalizeReady: boolean, jobStatus: string }}
 */
export function getFieldReadiness(wrap, options) {
  if (!wrap) {
    return { stepNavigable: true, finalizeReady: true, jobStatus: 'idle', characterReady: false, required: false };
  }
  const state = wrap._pbAiState || {};
  let cfg = {};
  try {
    const raw = wrap.dataset.settings || '';
    if (raw.trim().charAt(0) === '{') {
      cfg = JSON.parse(raw);
    } else {
      raw.split(/\r?\n/).forEach(function (line) {
        line = line.trim();
        if (!line) return;
        const idx = line.indexOf(':');
        if (idx === -1) return;
        const key = line.slice(0, idx).trim().replace(/\s+/g, '_');
        const val = line.slice(idx + 1).trim();
        if (key) cfg[key] = val;
      });
    }
  } catch (e) {}
  const inner = wrap.querySelector('.pb-upload-wrap');
  const hidden = inner ? inner.querySelector('input.builder-required[data-label]') : null;
  const reviewAcknowledged =
    options && options.reviewAcknowledged != null
      ? !!options.reviewAcknowledged
      : typeof window !== 'undefined' &&
        window.PBAiReviewHop &&
        typeof window.PBAiReviewHop.isReviewAcknowledged === 'function'
        ? window.PBAiReviewHop.isReviewAcknowledged(window.builderState, wrap.dataset?.label)
        : false;
  return computeAiImageReadiness(state, cfg, hidden, { reviewAcknowledged });
}

export function renderAiImagePreviewStage() {
  return (
    '<div class="pb-ai-image-preview-stage">' +
    '<div class="pb-ai-image-preview-error" style="display:none;" aria-live="polite"></div>' +
    '<div class="pb-ai-image-preview-body" data-store-ai-media data-store-media data-state="empty" data-media-active="false">' +
    '<div class="pb-ai-image-preview-loader" style="display:none;" aria-hidden="true">' +
    '<div class="pb-ai-image-preview-loader-inner">' +
    '<span class="pb-ai-image-preview-spinner"></span>' +
    '<p class="pb-ai-image-preview-loader-msg">Creating your character… This can take a few seconds.</p>' +
    '<button type="button" class="pb-ai-image-cancel-btn builder-btn meta-btn">Cancel</button>' +
    '</div></div>' +
    '<div class="pb-ai-image-preview-placeholder">Upload a photo to generate a preview</div>' +
    '<img class="pb-ai-image-preview-img" alt="" width="300" height="300" loading="lazy" style="display:none;">' +
    '<span class="store-media-fallback" role="img" aria-label="Artwork unavailable" hidden>Artwork unavailable</span>' +
    '<div class="pb-ai-image-revise-bar" style="display:none;" aria-hidden="true">' +
    '<input type="text" class="pb-ai-image-revise-input" placeholder="Edit this image…" maxlength="200" autocomplete="off" aria-label="Describe an edit for this image">' +
    '<button type="button" class="pb-ai-image-revise-send" aria-label="Apply edit">' +
    '<svg class="pb-ai-image-revise-send-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M12 19V5M12 5L6 11M12 5L18 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg></button></div></div></div>'
  );
}

/**
 * @param {object} deps — shared field-runtime helpers (avoids circular mega-body)
 */
export function createAiImageAdapter(deps) {
  const {
    fieldTypeLower,
    resolveEsc,
    resolveSelections,
    settingsToString,
    resolveRuntimeParseSettings,
    renderControlShell,
    bindUploadWrapInteractions,
    delegateInitStep,
  } = deps;

  return {
    id: 'ai-image',
    matches(field) {
      return fieldTypeLower(field).includes('ai image');
    },
    renderStep(ctx) {
      const field = ctx.field;
      const esc = resolveEsc(ctx);
      const settingsRaw = field.settings;
      const settingsStr = settingsToString(settingsRaw);
      const parseFn = resolveRuntimeParseSettings(ctx);
      const aiSettings = parseFn(settingsRaw);
      const aiRequired = !!(aiSettings && aiSettings.required);
      const reqAttr = aiRequired ? 'true' : 'false';
      const photoKey =
        (aiSettings.photo_property && String(aiSettings.photo_property).trim()) || 'Photo';
      const existingObj = resolveSelections(ctx)[field.label];
      let photoUrls = [];
      if (
        existingObj &&
        typeof existingObj === 'object' &&
        !Array.isArray(existingObj) &&
        existingObj[photoKey]
      ) {
        photoUrls = [String(existingObj[photoKey])];
      }
      const initialVal = JSON.stringify(photoUrls);
      const hasUploadVal = photoUrls.length > 0;
      const innerSettings = 'max_files:1, accept:photo, max_file_size:' + (aiSettings.max_file_size || 20) + (aiRequired ? ', required:true' : '');
      const innerHtml =
        '<div class="pb-ai-image-wrap" data-label="' +
        esc(field.label) +
        '" data-settings="' +
        esc(settingsStr) +
        '">' +
        '<div class="pb-ai-image-error" style="display:none;" aria-live="polite"></div>' +
        '<div class="pb-upload-wrap pb-upload-inner" data-label="' +
        esc(field.label) +
        '" data-settings="' +
        esc(innerSettings) +
        '">' +
        '<input type="hidden" class="builder-required" data-label="' +
        esc(field.label) +
        '" data-required="' +
        reqAttr +
        '" data-valid="' +
        (hasUploadVal ? 'true' : 'false') +
        '" value="' +
        esc(initialVal) +
        '">' +
        '<input type="file" class="pb-upload-file-input" style="display:none;" accept="' + PHOTO_ACCEPT + '">' +
        '<div class="pb-upload-row">' +
        '<div class="pb-upload-preview-list"></div>' +
        '<button type="button" class="pb-upload-empty">+</button>' +
        '</div>' +
        '<p class="pb-upload-hint">' + esc(uploadHint({accept:'photo',max_files:1,max_file_size:aiSettings.max_file_size})) + ' Large photos are optimized for AI.</p>' +
        '<div class="pb-upload-message" role="status" aria-live="polite" style="display:none;"></div>' +
        '</div>' +
        '<div class="pb-ai-image-field-status" role="status" aria-live="polite" style="display:none;"></div>' +
        '<div class="pb-ai-image-actions">' +
        '<div class="pb-ai-image-thumbs"></div>' +
        '<button type="button" class="builder-btn meta-btn pb-ai-image-retry" style="display:none;">Retry</button>' +
        '</div></div>';
      return {
        controlHtml: renderControlShell(field, innerHtml, ctx, {
          useQuestionHighlight: false,
        }),
        imageHtml: renderAiImagePreviewStage(),
        stepAttrs: ' data-ai-image="true"',
        extraClasses: '',
      };
    },
    initStep(stepEl) {
      delegateInitStep(stepEl, '.pb-ai-image-wrap', function (wrap) {
        const uploadWrap = wrap.querySelector('.pb-upload-wrap');
        if (uploadWrap) bindUploadWrapInteractions(uploadWrap);
        if (typeof window !== 'undefined' && window.PBAiImage?.init) {
          window.PBAiImage.init(wrap.closest('#main-flow-container, #sub-flow-container') || wrap, {
            force: false,
          });
        }
        if (typeof window !== 'undefined' && window.PBAiImage?.syncInFlightJob) {
          window.PBAiImage.syncInFlightJob(wrap);
        }
      });
    },
    validateStep(stepEl) {
      const wrap = stepEl?.querySelector?.('.pb-ai-image-wrap');
      if (wrap) {
        return getFieldReadiness(wrap).stepNavigable;
      }
      if (typeof window !== 'undefined' && window.PBAiImage?.validateStep) {
        return window.PBAiImage.validateStep(stepEl);
      }
      return true;
    },
    getFieldReadiness(wrap) {
      return getFieldReadiness(wrap);
    },
    hydrateStep(stepEl) {
      this.initStep(stepEl, { field: {} });
    },
    flushStep(stepEl) {
      delegateInitStep(stepEl, '.pb-ai-image-wrap', function (wrap) {
        if (typeof window !== 'undefined' && window.PBAiImage?.flushAllFromDom) {
          const root = wrap.closest('#main-flow-container, #sub-flow-container');
          window.PBAiImage.flushAllFromDom(root ? '#' + root.id : '#main-flow-container');
        }
      });
    },
  };
}

if (typeof window !== 'undefined') {
  window.PBFieldAiImage = {
    computeAiImageReadiness,
    getFieldReadiness,
    parseAiFieldSettings,
    savedAiEntryToReadinessState,
    resolveAiFinalizeReadyForSavedField,
    computeBundleRowAiFinalizeGaps,
    getReviewSessionId,
    isReviewOwedForCharacter,
  };
}
